
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowUpRight,
    CheckCircle2,
    Clock3,
    Share2,
    Target,
} from "lucide-react";
import caseStudies from "../data/casestudydata";
import banner from "../../assets/images/case_study_details.png";
import ShareModal from "../Addons/ShareModal";
export default function CaseStudyDetails() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [currentStudy, setCurrentStudy] = useState(null);
    const [relatedStudies, setRelatedStudies] = useState([]);
    const [shareOpen, setShareOpen] = useState(false);
   
    useEffect(() => {
        if (!slug || !caseStudies?.length) return;
        try {
            const cleanSlug = slug.replace(/^[/#]+/, "");
            const found = caseStudies.find(
                (item) => item.slug === cleanSlug
            );
            const related = caseStudies
                .filter((item) => item.slug !== cleanSlug)
                .slice(0, 3);
            setCurrentStudy(found || null);
            setRelatedStudies(related);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        } catch (error) {
            console.error("Error retrieving case study:", error);
        }
    }, [slug]);
    useEffect(() => {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }, []);
    if (!currentStudy) {
        return (
            <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                        <Target className="w-7 h-7 text-[#000036]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#000036] mb-2">
                        Case Study Not Found
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed mb-7">
                        The requested case study could not be located.
                        Please return to the case studies collection.
                    </p>
                    <button
                        onClick={() => navigate("/case-studies")}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#000036] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#11114d] transition-all"
                    >
                        <ArrowLeft size={15} />
                        Back to Case Studies
                    </button>
                </div>
            </div>
        );
    }
    const { content = {}, stats = [] } = currentStudy;
    const normalizeBlock = (block) => {
        if (!block) return null;
        return {
            intro: block.introPara || "",
            sections: block.sections || [],
            outro: block.outroPara || "",
        };
    };
    const challenge = normalizeBlock(content.challenge);
    const solution = normalizeBlock(content.solution);
    const result = normalizeBlock(content.result);
    const tableOfContents = [];
    if (content.introduction) {
        tableOfContents.push({
            id: "introduction",
            title: "Introduction",
        });
    }

    if (content.challenge) {
        tableOfContents.push({
            id: "challenge",
            title: "The Challenge",
        });
    }

    if (content.solution) {
        tableOfContents.push({
            id: "solution",
            title: "The Solution",
        });
    }

    if (content.result) {
        tableOfContents.push({
            id: "results",
            title: "Results & Impact",
        });
    }

    if (content.conclusion) {
        tableOfContents.push({
            id: "conclusion",
            title: "Conclusion",
        });
    }
    const renderContentBlock = ({
        id,
        title,
        block,
        accent,
    }) => {
        if (!block) return null;
        return (
            <section
                id={id}
                className="scroll-mt-8 space-y-7"
            >
                {/* Section Heading */}
                <div className="flex items-start gap-4">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#000036] mt-1">
                            {title}
                        </h2>
                    </div>
                </div>
                {/* Intro */}
                {block.intro && (
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                        {block.intro}
                    </p>
                )}
                {/* Internal Sections */}
                {block.sections?.map((section, index) => (
                    <div
                        key={index}
                        className="relative overflow-hidden rounded-2xl bg-white border border-slate-200/80 shadow-sm"
                    >
                        {/* Small accent */}
                        <div
                            className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
                        <div className="p-6 sm:p-7 pl-7 sm:pl-8">
                            {section.heading && (
                                <h3 className="text-lg sm:text-xl font-bold text-[#000036] mb-5">
                                    {section.heading}
                                </h3>
                            )}
                            {section.items?.length > 0 && (
                                <div className="space-y-3">
                                    {section.items.map((item, itemIndex) => (
                                        <div
                                            key={itemIndex}
                                            className="flex items-start gap-3"
                                        >
                                            <CheckCircle2
                                                size={17}
                                                className="text-[#3B9BD6] shrink-0 mt-0.5"
                                            />
                                            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                                                {item}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {/* Outro */}
                {block.outro && (
                    <p className="text-base text-slate-600 leading-relaxed">
                        {block.outro}
                    </p>
                )}
            </section>
        );
    };

    
    return (<>


        <article className="relative w-full min-h-screen bg-[#F7F9FC] text-slate-900 ">
            <header className="relative overflow-hidden bg-white px-6 sm:px-8 lg:px-12 pt-36 sm:pt-40 pb-20">
                <div
                    className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                    style={{
                        background:
                            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                    }}
                />
                {/* Grid */}
                {/* <div
                    className="absolute inset-0 opacity-[0.035] pointer-events-none"
                    style={{
                        backgroundImage:
                            "linear-gradient(#000036 1px, transparent 1px), linear-gradient(90deg, #000036 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                    }}
                /> */}
                <div className="absolute -bottom-8 -left-8 w-[420px] sm:w-[520px] opacity-[0.12] pointer-events-none">
                    <img
                        src={banner}
                        alt=""
                        className="w-full object-contain"
                    />
                </div>
                <div className="max-w-5xl mx-auto relative z-10 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-extrabold tracking-tight text-[#000036] leading-[1.08] max-w-4xl mx-auto"
                    >
                        {currentStudy.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.5,
                            delay: 0.1,
                        }}
                        className="mt-6 text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
                    >
                        {currentStudy.summary}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.5,
                            delay: 0.2,
                        }}
                        className="mt-9 flex flex-wrap items-center justify-center gap-3"
                    >
                        {currentStudy.industry && (
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#000036] text-white text-xs font-semibold">

                                {currentStudy.industry}
                            </span>
                        )}

                        {currentStudy.capability && (
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-[#000036] text-xs font-semibold shadow-sm">

                                {currentStudy.capability}
                            </span>
                        )}

                        {currentStudy.duration && (
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold shadow-sm">
                                <Clock3 size={14} />
                                {currentStudy.duration}
                            </span>
                        )}
                    </motion.div>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-24">
                        {/* Back */}
                        <Link
                            to="/case-studies"
                            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#000036] hover:text-[#3B9BD6] transition-colors group"
                        >
                            <ArrowLeft
                                size={15}
                                className="group-hover:-translate-x-1 transition-transform"
                            />

                            Back to all Case Studies
                        </Link>
                        {stats?.length > 0 && (
                            <div className="rounded-2xl bg-[#000036] text-white overflow-hidden shadow-lg">
                                <div className="px-6 py-5 border-b border-white/10">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300">
                                            Benchmark Telemetry
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6 grid grid-cols-2 gap-5">
                                    {stats.map((item, index) => (
                                        <div
                                            key={index}
                                            className=" border-b border-white/10 last:border-0"
                                        >
                                            <div className="text-3xl font-black font-serif text-white tracking-tight">
                                                {item.number}
                                            </div>
                                            <div className="mt-1 text-sm  text-slate-300 leading-snug">
                                                {item.tagline}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {tableOfContents.length > 0 && (
                            <div className="rounded-2xl bg-white border border-slate-200/80 shadow-sm p-6">
                                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                    On This Page
                                </span>
                                <nav className="mt-4 space-y-1">
                                    {tableOfContents.map((item, index) => (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="block text-sm font-medium text-slate-600 hover:text-[#000036] hover:translate-x-1 transition-all py-1"
                                        >
                                            {1 + index}.  {item.title}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        )}
                        <button
                            onClick={() => setShareOpen(true)}
                            className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-[#EAF7FC] border border-[#C9EDF8] text-[#000036] hover:bg-[#DDF3FA] transition-colors"
                        >
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                                <Share2 size={16} />
                               Share Case Study
                            </span>
                            <ArrowUpRight size={16} />
                        </button>
                    </aside>
                    <div className="lg:col-span-8 space-y-9">
                        {/* Main Image */}
                        {currentStudy.image && (
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 15,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.5,
                                }}
                                className="relative h-60 sm:h-80 lg:h-[430px] mt-10 rounded-3xl overflow-hidden bg-slate-100 border border-slate-200 shadow-sm"
                            >
                                <img
                                    src={currentStudy.image}
                                    alt={currentStudy.title}
                                    className="w-full h-full object-cover"
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-[#000036]/30 via-transparent to-transparent pointer-events-none" />
                            </motion.div>
                        )}
                        {/* Key Takeaway */}
                        <div className="relative overflow-hidden rounded-3xl bg-[#000036] text-white p-7 sm:p-9 shadow-xl">
                            <div className="relative">
                                <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                                    {content.introduction ||
                                        currentStudy.summary}
                                </p>
                            </div>
                        </div>
                        {/* Introduction */}
                        {content.introduction && (
                            <section
                                id="introduction"
                                className="scroll-mt-28 space-y-5 pt-3"
                            >
                                <div className="flex items-center gap-3">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-[#000036]">
                                        Introduction
                                    </h2>
                                </div>
                                <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                    {content.introduction}
                                </p>
                            </section>
                        )}
                        {/* Challenge */}
                        {renderContentBlock({
                            id: "challenge",
                            number: "01",
                            title: "The Challenge",
                            block: challenge,

                            accent: "bg-amber-500",
                        })}

                        {/* Solution */}
                        {renderContentBlock({
                            id: "solution",
                            number: "02",
                            title: "The Solution",
                            block: solution,

                            accent: "bg-[#2479A8]",
                        })}

                        {/* Results */}
                        {renderContentBlock({
                            id: "results",
                            number: "03",
                            title: "Results & Business Impact",
                            block: result,

                            accent: "bg-emerald-500",
                        })}

                        {/* Conclusion */}
                        {content.conclusion && (
                            <section
                                id="conclusion"
                                className="">
                                <div className="relative">
                                    <h2 className="text-2xl sm:text-3xl font-bold mt-3 mb-5">
                                        Conclusion
                                    </h2>
                                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                                        {content.conclusion}
                                    </p>
                                </div>
                            </section>
                        )}
                        {/* CTA */}
                        <div className="mt-12 pt-8 border-t border-slate-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                                <div>
                                    <h3 className="text-xl font-bold text-[#000036] mt-1">
                                        Let's build something meaningful.
                                    </h3>
                                </div>
                                <Link
                                    to="/contact"
                                    className="group relative inline-flex items-center justify-between gap-3 px-5 py-2 border border-black/15 bg-slate-950 text-white font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-white hover:text-slate-700 hover:shadow-lg"
                                    style={{
                                        borderRadius: "999px",
                                    }}
                                >
                                    <span>Discuss Your Project</span>
                                    <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5 group-hover:bg-slate-700 group-hover:text-white">
                                        <ArrowUpRight size={15} />
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </article>
        <ShareModal isOpen={shareOpen}
            onClose={() => setShareOpen(false)}
            url={window.location.href}
            title={currentStudy.title}
            description={currentStudy.summary} />
    </>
    );
}

